import { useEffect, useMemo, useState } from 'react';
import { Box, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { Reorder } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getConfigFromStorage } from '@/utils/config';
import { RosterGroup } from '@/model';
import { RosterGroupRow } from './RosterGroupRow';

export interface RosterGroupTableProps {
  data: RosterGroup[];
  isLoading: boolean;
  isReorderInFlight?: boolean;
  onEditClick: (item: RosterGroup) => void;
  onDeleteClick: (item: RosterGroup) => void;
  onReorder: (orderedUuids: string[]) => void;
}

export const RosterGroupTable = ({
  data,
  isLoading,
  isReorderInFlight = false,
  onEditClick,
  onDeleteClick,
  onReorder,
}: RosterGroupTableProps) => {
  const { t } = useTranslation();
  const isMultiTeam = getConfigFromStorage()?.is_multi_team ?? true;

  const [order, setOrder] = useState<string[]>(() => data.map(item => item.uuid));

  useEffect(() => {
    setOrder(data.map(item => item.uuid));
  }, [data]);

  const itemsByUuid = useMemo(() => {
    const map = new Map<string, RosterGroup>();
    data.forEach(item => map.set(item.uuid, item));
    return map;
  }, [data]);

  const orderedItems = useMemo(
    () => order.map(uuid => itemsByUuid.get(uuid)).filter((item): item is RosterGroup => !!item),
    [order, itemsByUuid],
  );

  const handleReorder = (nextIds: string[]) => {
    if (isReorderInFlight) return;
    setOrder(nextIds);

    let same = nextIds.length === order.length;
    if (same) {
      for (let i = 0; i < nextIds.length; i += 1) {
        if (nextIds[i] !== order[i]) {
          same = false;
          break;
        }
      }
    }
    if (same) return;
    onReorder(nextIds);
  };

  return (
    <Box borderWidth='1px' borderColor='gray.100' borderRadius='md' overflowX='auto' bg='white'>
      <Text fontSize='xs' color='gray.500' px={4} pt={3} pb={2}>
        {t('pages.application_roster_group.components.roster_group_table.roster_group_table_reorder_hint')}
      </Text>

      <Table size='sm' variant='simple'>
        <Thead sx={{ backgroundColor: 'var(--chakra-colors-gray-100) !important' }}>
          <Tr>
            <Th px={2} w='32px' sx={{ backgroundColor: 'var(--chakra-colors-gray-100) !important' }} />
            {isMultiTeam ? (
              <Th
                px={3}
                sx={{
                  color: 'var(--chakra-colors-gray-800) !important',
                  backgroundColor: 'var(--chakra-colors-gray-100) !important',
                }}
              >
                {t('pages.application_roster_group.components.roster_group_table.roster_group_table_team')}
              </Th>
            ) : null}
            <Th
              px={3}
              sx={{
                color: 'var(--chakra-colors-gray-800) !important',
                backgroundColor: 'var(--chakra-colors-gray-100) !important',
              }}
            >
              {t('pages.application_roster_group.components.roster_group_table.roster_group_table_name')}
            </Th>
            <Th
              px={3}
              textAlign='center'
              sx={{
                color: 'var(--chakra-colors-gray-800) !important',
                backgroundColor: 'var(--chakra-colors-gray-100) !important',
              }}
            >
              {t('pages.application_roster_group.components.roster_group_table.roster_group_table_sort_order')}
            </Th>
            <Th
              px={3}
              textAlign='center'
              sx={{
                color: 'var(--chakra-colors-gray-800) !important',
                backgroundColor: 'var(--chakra-colors-gray-100) !important',
              }}
            >
              {t('pages.application_roster_group.components.roster_group_table.roster_group_table_show_stats')}
            </Th>
            <Th
              px={3}
              sx={{
                color: 'var(--chakra-colors-gray-800) !important',
                backgroundColor: 'var(--chakra-colors-gray-100) !important',
              }}
            >
              {t('pages.application_roster_group.components.roster_group_table.roster_group_table_created_at')}
            </Th>
            <Th
              px={2}
              textAlign='right'
              sx={{
                color: 'var(--chakra-colors-gray-800) !important',
                backgroundColor: 'var(--chakra-colors-gray-100) !important',
              }}
            >
              {t('pages.application_roster_group.components.roster_group_table.roster_group_table_actions')}
            </Th>
          </Tr>
        </Thead>

        <Reorder.Group as='tbody' axis='y' values={order} onReorder={handleReorder}>
          {orderedItems.map(item => (
            <RosterGroupRow
              key={item.uuid}
              item={item}
              isMultiTeam={isMultiTeam}
              isDragDisabled={isReorderInFlight}
              onEditClick={onEditClick}
              onDeleteClick={onDeleteClick}
            />
          ))}
        </Reorder.Group>

        {!isLoading && orderedItems.length === 0 ? (
          <Tbody>
            <Tr>
              <Td colSpan={isMultiTeam ? 7 : 6} textAlign='center' py={6} color='gray.500'>
                {t('pages.application_roster_group.components.roster_group_table.roster_group_table_empty')}
              </Td>
            </Tr>
          </Tbody>
        ) : null}
      </Table>
    </Box>
  );
};
